import isDev from "./isDev"

type ConfigOption = "APP_WWW_URL" | "AUTH_URL" | "PUSHER_KEY" | "PUSHER_CLUSTER"

const defaultConfig: Record<ConfigOption, string> = {
  // eslint-disable-next-line custom/no-hardcoded-brand-name -- environment config
  APP_WWW_URL: "https://api.tierzero.ai",
  // eslint-disable-next-line custom/no-hardcoded-brand-name -- environment config
  AUTH_URL: "https://auth.tierzero.ai",
  PUSHER_KEY: "",
  PUSHER_CLUSTER: "",
}

const parseBoolean = (value: string | undefined | null, fallback: boolean): boolean => {
  if (value === undefined || value === null) {
    return fallback;
  }

  switch (value.trim().toLowerCase()) {
    case "true":
    case "1":
    case "yes":
    case "y":
    case "on":
      return true;
    case "false":
    case "0":
    case "no":
    case "n":
    case "off":
      return false;
    default:
      return fallback;
  }
};

const sanitizeUrl = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  try {
    return new URL(value).toString();
  } catch {
    return null;
  }
};

const trimTrailingSlash = (value: string): string => {
  if (value.endsWith("/")) {
    return value.replace(/\/+$/, "");
  }
  return value;
};

const resolveAuthUrl = (): string => {
  const useMockPropelAuth = parseBoolean(import.meta.env.VITE_USE_MOCK_PROPELAUTH, false);
  const providedAuthUrl = sanitizeUrl(import.meta.env.VITE_AUTH_URL);
  const providedBackendUrl = sanitizeUrl(import.meta.env.VITE_PUBLIC_BACKEND_URL);

  if (useMockPropelAuth) {
    const base = trimTrailingSlash(
      providedBackendUrl || (isDev ? "http://localhost:8001" : defaultConfig.APP_WWW_URL),
    );
    return `${base}/mock_propel_auth`;
  }

  if (providedAuthUrl) {
    return trimTrailingSlash(providedAuthUrl);
  }

  return defaultConfig.AUTH_URL;
};

const actualConfig: Partial<Record<ConfigOption, string>> = {
  APP_WWW_URL: import.meta.env.VITE_PUBLIC_BACKEND_URL,
  PUSHER_KEY: import.meta.env.VITE_PUSHER_KEY,
  PUSHER_CLUSTER: import.meta.env.VITE_PUSHER_CLUSTER,
}

const config = {
  get(key: ConfigOption): string {
    if (key === "AUTH_URL") {
      const resolvedAuthUrl = resolveAuthUrl();
      const hasAuthUrl = !!resolvedAuthUrl;

      if (isDev && hasAuthUrl) {
        return resolvedAuthUrl;
      }

      return hasAuthUrl ? resolvedAuthUrl : defaultConfig.AUTH_URL;
    }

    const envValue = actualConfig[key];
    const hasVariableSetInEnvironment = !!envValue;

    if (isDev && hasVariableSetInEnvironment) {
      return envValue as string;
    }

    return hasVariableSetInEnvironment ? (envValue as string) : defaultConfig[key];
  },
}

export const useMockPropelAuth = parseBoolean(import.meta.env.VITE_USE_MOCK_PROPELAUTH, false);

export default config
